import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Grid,
  RadioGroup,
  Radio,
  Autocomplete,
  FormControl,
  FormLabel,
  Dialog,
  DialogContent,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Close } from "@mui/icons-material";

const SHORTCUT_TABLE_LABEL = [
  {
    id: 1,
    label: "Short Form",
    value: "shortForm",
  },
  {
    id: 2,
    label: "Value",
    value: "value",
  },
  {
    id: 3,
    label: "",
    value: "",
  },
];
export default function MasterDynamicTemplateSettingsNumericField({
  open,
  onClose,
  selectedSubRowType,
  addingSubRowTo,
  dynamicForm,
  setDynamicForm,
  editingRow,
}) {
  const isNumeric =
    editingRow?.rowData?.type === "Numeric" || selectedSubRowType === "Numeric";

  const [formData, setFormData] = useState({
    nameFirst: true,
    category: "Both",
    ...(isNumeric ? { allowNormalRangeCheck: true } : {}),
  });

  const [shortcuts, setShortcuts] = useState([]);
  const [ranges, setRanges] = useState([
    {
      operation: null,
      value1: 0,
      value2: 0,
      bold: true,
      critical: true,
      invalid: true,
      interpretation: "",
    },
  ]);

  // formdata section
  const handleChange = (e, key = null) => {
    const { name, value, type, checked } = e.target;

    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    setFormData((prevValue) => {
      // Ensure key is a valid string
      if (key && typeof key === "string") {
        return {
          ...prevValue,
          [key]: {
            ...prevValue?.[key], // Preserve existing key values
            [name]: parsedValue, // Update the specific field inside key
          },
        };
      } else {
        return {
          ...prevValue,
          [name]: parsedValue, // Update directly in formData
        };
      }
    });
  };

  // range section
  const addItem = () => {
    setRanges((prev) => [
      ...prev,
      {
        operation: null,
        value1: 0,
        value2: 0,
        bold: true,
        critical: true,
        invalid: true,
        interpretation: "",
      },
    ]);
  };
  const handleRemoveItem = (index) => {
    setRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChangeRange = (e, index) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    setRanges((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [name]: parsedValue } : item
      )
    );
  };

  // shortcut section
  const handleAddTableRow = (state, setState, newRowTemplate) => {
    setState([...state, newRowTemplate]); // Ensure correct structure
  };

  const handleRemoveTableRow = (rowIndex, state, setState) => {
    setState(state.filter((_, i) => i !== rowIndex));
  };

  const handleChangeShortcut = (e, rowIndex, setState) => {
    const { name, value } = e.target;

    setState((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, [name]: value } : row))
    );
  };

  // submit section
  const handleSubmit = async (e) => {
    e.preventDefault();
    const mainIndex = addingSubRowTo;

    const formattedRanges = ranges?.map(
      ({ operation, value1, value2, ...rest }) => {
        if (operation === "Between") {
          return { operation, value1, value2, ...rest };
        } else if (operation === "Less Than") {
          return { operation, value1: value2, ...rest }; // Send value2 as value1
        }
        return { operation, value1, ...rest }; // Exclude value2 for Greater Than
      }
    );
    setDynamicForm(
      dynamicForm?.map((row, i) =>
        i === mainIndex
          ? {
              ...row,
              fields: [
                ...row.fields,
                {
                  ...formData,
                  type: editingRow?.rowData?.type || selectedSubRowType,
                  settings: {
                    ...formData?.settings,
                    ranges: formattedRanges,
                    shortcuts,
                  },
                },
              ],
            }
          : row
      )
    );

    onClose();
  };

  useEffect(() => {
    setFormData(
      editingRow?.rowData || {
        nameFirst: true,
        category: "Both",
        allowNormalRangeCheck: true,
      }
    );
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            bgcolor: "#fff",
            width: { xs: "95vw", lg: "1100px" },
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent
        sx={{
          "& .MuiFormLabel-asterisk": {
            color: "error.main",
          },
        }}
      >
        {/* Heading and Close Button */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#5D6A82",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "inherit" }}>
            Dynamic Template Setting
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          {/* add dynamic form fields */}
          <Box
            sx={{
              bgcolor: "#E5F4FF",
              borderRadius: "12px",
              p: "20px",
            }}
          >
            <Typography sx={{ mb: "20px" }}>Add Dynamic Form Fields</Typography>
            <Grid container spacing={2}>
              {/* displayText */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Display Text"
                  name="displayText"
                  value={formData?.displayText || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* elementName */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="elementName"
                  label="Element Name"
                  value={formData?.elementName || ""}
                  onChange={handleChange}
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* code */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Code"
                  name="code"
                  value={formData?.code || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* Name First or Value First */}
              <Grid item xs={12} sm={6} md={4}>
                <RadioGroup
                  row
                  value={formData?.nameFirst ? "nameFirst" : "valueFirst"}
                  onChange={(e) => {
                    handleChange({
                      target: {
                        name: "nameFirst",
                        value: e.target.value === "nameFirst",
                      },
                    });
                    handleChange({
                      target: {
                        name: "valueFirst",
                        value: e.target.value === "valueFirst",
                      },
                    });
                  }}
                  aria-labelledby="orderPreference-label"
                >
                  <FormControlLabel
                    label="Name First"
                    value="nameFirst"
                    control={<Radio />}
                  />
                  <FormControlLabel
                    label="Value First"
                    value="valueFirst"
                    control={<Radio />}
                  />
                </RadioGroup>
              </Grid>

              {/* unit */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Unit"
                  name="unit"
                  value={formData?.unit || ""}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* templateType */}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Template Type"
                  name="templateType"
                  value={formData?.templateType || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              {/* allow normal range check */}
              <Grid item xs={12}>
                {isNumeric ? (
                  <FormControlLabel
                    label="Allow Normal Range Check"
                    control={
                      <Checkbox
                        name="allowNormalRangeCheck"
                        checked={formData?.allowNormalRangeCheck || false}
                        onChange={handleChange}
                      />
                    }
                  />
                ) : (
                  <FormControlLabel
                    label="Shortcut"
                    defaultChecked
                    control={<Checkbox name="shortcut" />}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Categorize Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              my: "20px",
            }}
          >
            <Box
              sx={{
                bgcolor: "#E5F4FF",
                borderRadius: "12px",
                width: "100%",
                p: "20px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel id="categorize-label">Categorize</FormLabel>
                    <RadioGroup
                      row
                      name="category"
                      value={formData?.settings?.category || "Both"} // Controlled value
                      onChange={(e) => handleChange(e, "settings")}
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-evenly",
                      }}
                    >
                      <FormControlLabel
                        value="Both"
                        control={<Radio />}
                        label="Both"
                      />
                      <FormControlLabel
                        value="Male"
                        control={<Radio />}
                        label="Male"
                      />
                      <FormControlLabel
                        value="Female"
                        control={<Radio />}
                        label="Female"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Group By Age */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="groupByAge"
                        checked={formData?.settings?.groupByAge || false} // Ensure controlled state
                        onChange={(e) => handleChange(e, "settings")}
                      />
                    }
                    label="Group By Age"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* operation or shortcut */}
          {isNumeric ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                my: "20px",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#E5F4FF",
                  borderRadius: "12px",
                  width: "100%",

                  p: "15px",
                }}
              >
                <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                  Operation
                </Typography>

                {ranges?.map((item, index) => (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    sx={{ mb: "20px" }}
                    alignItems="center"
                  >
                    {/* Operation */}
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={
                        item?.operation === null ||
                        item?.operation === "Between"
                          ? 2
                          : 3
                      }
                    >
                      <Autocomplete
                        options={["Between", "Greater Than", "Less Than"]}
                        value={item?.operation || ""}
                        onChange={(e, newValue) =>
                          handleChangeRange(
                            { target: { name: "operation", value: newValue } },
                            index
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="operation"
                            label="Operation"
                            required
                            fullWidth
                          />
                        )}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>

                    {/* Value Inputs */}
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={
                        item?.operation === null ||
                        item?.operation === "Between"
                          ? 3
                          : 2
                      }
                    >
                      <Grid container spacing={1}>
                        {(item?.operation === null ||
                          item?.operation === "Between" ||
                          item?.operation === "Greater Than") && (
                          <Grid
                            item
                            xs={12}
                            sm={
                              item?.operation === null ||
                              item?.operation === "Between"
                                ? 6
                                : 12
                            }
                          >
                            <TextField
                              name="value1"
                              label="Value >"
                              type="number"
                              value={item?.value1 || ""}
                              onChange={(e) => handleChangeRange(e, index)}
                              required
                              fullWidth
                              sx={{ bgcolor: "white" }}
                            />
                          </Grid>
                        )}
                        {(item?.operation === null ||
                          item?.operation === "Between" ||
                          item?.operation === "Less Than") && (
                          <Grid
                            item
                            xs={12}
                            sm={
                              item?.operation === null ||
                              item?.operation === "Between"
                                ? 6
                                : 12
                            }
                          >
                            <TextField
                              name="value2"
                              label="Value <"
                              type="number"
                              value={item?.value2 || ""}
                              onChange={(e) => handleChangeRange(e, index)}
                              required
                              fullWidth
                              sx={{ bgcolor: "white" }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>

                    {/* Checkbox Options */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="bold"
                                checked={item?.bold || false}
                                onChange={(e) => handleChangeRange(e, index)}
                              />
                            }
                            sx={{ height: "57px" }}
                            label="Bold"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="critical"
                                checked={item?.critical || false}
                                onChange={(e) => handleChangeRange(e, index)}
                              />
                            }
                            sx={{ height: "57px" }}
                            label="Critical"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="invalid"
                                checked={item?.invalid || false}
                                onChange={(e) => handleChangeRange(e, index)}
                              />
                            }
                            sx={{ height: "57px" }}
                            label="Invalid"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Interpretation */}
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        name="interpretation"
                        label="Interpretation"
                        value={item?.interpretation || ""}
                        onChange={(e) => handleChangeRange(e, index)}
                        required
                        fullWidth
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>

                    {/* Remove Button */}
                    {ranges?.length > 1 && (
                      <Grid item xs={1}>
                        <IconButton onClick={() => handleRemoveItem(index)}>
                          <CloseIcon />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                ))}

                <Button
                  sx={{
                    textTransform: "none",
                    color: "rgba(146, 166, 204, 1)",
                  }}
                  onClick={addItem}
                >
                  + Add item
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                border: `1px solid #CCD4DA`,
                borderRadius: "12px",
                mb: "30px",
                p: "20px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: "20px",
                }}
              >
                <Typography>Shortcut</Typography>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead
                    sx={{
                      bgcolor: "#EAEAEA",
                    }}
                  >
                    <TableRow>
                      {SHORTCUT_TABLE_LABEL?.map((option) => (
                        <TableCell
                          key={option?.id}
                          sx={{
                            textAlign: option?.align || "left",
                            color: "#5D6A82",
                          }}
                        >
                          {option?.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shortcuts?.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {SHORTCUT_TABLE_LABEL?.map((column, columnIndex) => (
                          <TableCell
                            key={`${rowIndex}${columnIndex}`}
                            sx={{ color: "#5D6A82" }}
                          >
                            {columnIndex ===
                            SHORTCUT_TABLE_LABEL?.length - 1 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mr: "15px",
                                }}
                              >
                                <Close
                                  sx={{ cursor: "pointer", fontSize: "18px" }}
                                  onClick={() =>
                                    handleRemoveTableRow(
                                      rowIndex,
                                      shortcuts,
                                      setShortcuts
                                    )
                                  }
                                />
                              </Box>
                            ) : (
                              <TextField
                                label={column?.label}
                                required
                                name={column?.value}
                                value={row[column?.value] || ""}
                                onChange={(e) =>
                                  handleChangeShortcut(
                                    e,
                                    rowIndex,
                                    setShortcuts
                                  )
                                }
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        colSpan={SHORTCUT_TABLE_LABEL?.length}
                        sx={{ color: "#92A6CC", cursor: "pointer" }}
                        onClick={() =>
                          handleAddTableRow(shortcuts, setShortcuts, {
                            shortForm: "",
                            value: "",
                          })
                        }
                      >
                        + Add item
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              my: "20px",
            }}
          >
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
